import DirectionsIcon from '@mui/icons-material/Directions';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { usePlacePhotoUrls } from '../../../../hooks/usePlacePhotoUrls';
import { TripList } from '../../../../redux/trip/trip.types';
import { getOpenStatus } from '../../../../utils/Card';
import StarRating from '../../../StarRating/StarRating';
import MapAddToListPanel from './MapAddToListPanel';
import PhotoLightbox from './PhotoLightbox';

import './MapPoiPopup.scss';

type MapPoiPopupProps = {
  lat: number; // required by google-map-react for overlay positioning
  lng: number; // required by google-map-react for overlay positioning
  placeId: string;
  name: string;
  placeType?: string;
  openingHours?: string[];
  photoReferences?: string[];
  rating?: number;
  reviewCount?: number;
  isLoading: boolean;
  onClose: () => void;
  lists?: TripList[];
  checkedListIds?: Set<string>;
  onAddToList?: (listId: string) => Promise<void>;
};

function MapPoiPopup({
  placeId,
  name,
  placeType,
  openingHours,
  photoReferences,
  rating,
  reviewCount,
  isLoading,
  onClose,
  lists,
  checkedListIds,
  onAddToList,
}: MapPoiPopupProps) {
  const [isAddToListOpen, setIsAddToListOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const { urls: resolvedPhotoUrls } = usePlacePhotoUrls(photoReferences, undefined);
  const photos = resolvedPhotoUrls.slice(0, 3);
  const openStatus = getOpenStatus(openingHours);
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(name)}&destination_place_id=${placeId}`;
  const showAddToList = Boolean(onAddToList);
  const hasLists = Boolean(lists && lists.length > 0);

  // Reset panel whenever the selected POI changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional – reset on placeId change only
  useEffect(() => {
    setIsAddToListOpen(false);
    setLightboxIndex(null);
  }, [placeId]);

  return (
    // onTouchStart/End needed on mobile: Google Maps fires its click listener from touchend,
    // so without stopping touch events the map dismisses the popup when tapping inside it.
    <div
      className='map-poi-popup__wrapper'
      onClick={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
      role='presentation'
    >
      <button
        className='map-poi-popup__close'
        onClick={onClose}
        type='button'
        aria-label='Close'
      >
        Close X
      </button>
      <div className='map-poi-popup'>
        {isLoading ? (
          <span className='map-poi-popup__loading'>Loading...</span>
        ) : (
          <>
            {photos.length > 0 && (
              <div className='map-poi-popup__photos'>
                {photos.map((url, i) => (
                  // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard nav handled by PhotoLightbox
                  <img
                    key={url}
                    className='map-poi-popup__photo'
                    src={url}
                    alt={name}
                    onClick={() => setLightboxIndex(i)}
                  />
                ))}
              </div>
            )}

            <div className='map-poi-popup__body'>
              <div className='map-poi-popup__content'>
                <span className='map-poi-popup__name'>{name}</span>

                {rating !== undefined && (
                  <div className='map-poi-popup__rating'>
                    <span className='map-poi-popup__rating-value'>{rating.toFixed(1)}</span>
                    <StarRating
                      rating={rating}
                      size='small'
                    />
                    {reviewCount !== undefined && (
                      <span className='map-poi-popup__rating-count'>
                        ({reviewCount.toLocaleString()})
                      </span>
                    )}
                  </div>
                )}

                {placeType && <span className='map-poi-popup__type'>{placeType}</span>}

                {openStatus && (
                  <span className='map-poi-popup__open-status'>
                    {openStatus.isOpen ? (
                      openStatus.label
                    ) : (
                      <>
                        <span className='map-poi-popup__open-status--closed'>Closed</span>
                        {openStatus.nextOpenTime && ` · Opens ${openStatus.nextOpenTime}`}
                      </>
                    )}
                  </span>
                )}
              </div>

              <a
                className='map-poi-popup__directions'
                href={directionsUrl}
                target='_blank'
                rel='noopener noreferrer'
                aria-label='Get directions'
              >
                <DirectionsIcon fontSize='medium' />
              </a>
            </div>

            <div className='map-poi-popup__footer'>
              {showAddToList && (
                <div className='map-poi-popup__add-to-list-row'>
                  <button
                    className='map-poi-popup__add-to-list'
                    type='button'
                    disabled={!hasLists}
                    onClick={() => hasLists && setIsAddToListOpen((prev) => !prev)}
                  >
                    + Add to List
                  </button>
                  {!hasLists && (
                    <Tooltip
                      title='Create a list to start saving places'
                      placement='top'
                      arrow
                    >
                      <InfoOutlinedIcon className='map-poi-popup__no-lists-info' />
                    </Tooltip>
                  )}
                </div>
              )}
            </div>

            {isAddToListOpen && showAddToList && hasLists && (
              <MapAddToListPanel
                lists={lists!}
                checkedListIds={checkedListIds ?? new Set()}
                onToggle={onAddToList!}
                onClose={() => setIsAddToListOpen(false)}
              />
            )}
          </>
        )}
      </div>

      {lightboxIndex !== null && (
        <PhotoLightbox
          photos={photos}
          initialIndex={lightboxIndex}
          alt={name}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}

MapPoiPopup.displayName = 'MapPoiPopup';
export default MapPoiPopup;
